import { Controller } from '@hotwired/stimulus'
import {
  setupShelfMarquee,
  pauseShelfMarquee,
  resumeShelfMarquee,
  prefersReducedMotion
} from '../utils/shelf_marquee'

export default class extends Controller {
  static targets = ['track', 'slide']
  static values = {
    interval: { type: Number, default: 2000 },
    marquee: { type: Boolean, default: false },
    marqueeDuration: { type: Number, default: 30 },
    loopCount: Number
  }

  connect () {
    if (this.marqueeValue) {
      this.connectMarquee()
      return
    }

    this.index = 0
    this.cloneLoopSlides()
    this.enableTransition()
    this.moveToIndex(0, false)

    if (!this.canSlide()) return

    this.start()
    this.handleResize = () => this.moveToIndex(this.index, false)
    this.element.addEventListener('mouseenter', this.pause)
    this.element.addEventListener('mouseleave', this.resume)
    window.addEventListener('resize', this.handleResize)
  }

  connectMarquee () {
    if (!this.hasTrackTarget || this.loopCount() < 2) return

    this.pauseMarquee = () => pauseShelfMarquee(this.trackTarget)
    this.resumeMarquee = () => resumeShelfMarquee(this.trackTarget)

    setupShelfMarquee(this.trackTarget, {
      durationSeconds: this.marqueeDurationValue,
      reducedMotion: this.reducedMotion
    })

    this.element.classList.add('shelf-marquee')
    this.element.addEventListener('mouseenter', this.pauseMarquee)
    this.element.addEventListener('mouseleave', this.resumeMarquee)
    this.trackTarget.addEventListener('touchstart', this.pauseMarquee, { passive: true })
    this.trackTarget.addEventListener('touchend', this.resumeMarquee, { passive: true })
  }

  cloneLoopSlides () {
    if (this.reducedMotion) return
    if (this.slideTargets.length > this.loopCount()) return

    this.slideTargets.slice(0, this.loopCount()).forEach((slide) => {
      const clone = slide.cloneNode(true)
      clone.setAttribute('aria-hidden', 'true')
      this.trackTarget.appendChild(clone)
    })
  }

  disconnect () {
    if (this.marqueeValue) {
      this.element.removeEventListener('mouseenter', this.pauseMarquee)
      this.element.removeEventListener('mouseleave', this.resumeMarquee)
      this.trackTarget?.removeEventListener('touchstart', this.pauseMarquee)
      this.trackTarget?.removeEventListener('touchend', this.resumeMarquee)
      return
    }

    this.stop()
    this.clearResetTimer()
    this.clearResumeTimer()
    this.element.removeEventListener('mouseenter', this.pause)
    this.element.removeEventListener('mouseleave', this.resume)
    window.removeEventListener('resize', this.handleResize)
  }

  next (event) {
    event?.preventDefault()
    if (this.marqueeValue) return
    if (!this.canSlide()) return

    this.pause()
    this.advance()
    this.scheduleResume()
  }

  prev (event) {
    event?.preventDefault()
    if (this.marqueeValue) return
    if (!this.canSlide()) return

    this.pause()
    this.clearResetTimer()

    if (this.index <= 0) {
      this.disableTransition()
      this.index = this.loopCount()
      this.moveToIndex(this.index, false)
      this.trackTarget.offsetHeight
      this.enableTransition()
    }

    this.index -= 1
    this.moveToIndex(this.index)
    this.scheduleResume()
  }

  pause = () => {
    this.stop()
  }

  resume = () => {
    if (!this.canSlide()) return
    this.start()
  }

  advance () {
    const loopCount = this.loopCount()
    this.index += 1
    this.moveToIndex(this.index)

    if (this.index >= loopCount) {
      this.clearResetTimer()
      this.resetTimer = window.setTimeout(() => {
        this.resetToStart()
      }, 720)
    }
  }

  moveToIndex (index, transition = true) {
    const slide = this.slideTargets[index]
    if (!slide || !this.hasTrackTarget) return

    if (transition) {
      this.enableTransition()
    } else {
      this.disableTransition()
    }

    this.trackTarget.style.transform = `translateX(-${slide.offsetLeft}px)`
  }

  resetToStart () {
    this.index = 0
    this.disableTransition()
    this.moveToIndex(0, false)
    this.trackTarget.offsetHeight
    this.enableTransition()
  }

  enableTransition () {
    if (!this.hasTrackTarget) return

    this.trackTarget.style.transition = 'transform 650ms ease'
  }

  disableTransition () {
    if (!this.hasTrackTarget) return

    this.trackTarget.style.transition = 'none'
  }

  loopCount () {
    return this.hasLoopCountValue ? this.loopCountValue : this.slideTargets.length
  }

  canSlide () {
    if (this.loopCount() < 2) return false
    if (this.reducedMotion) return false
    return true
  }

  start () {
    this.stop()
    this.timer = window.setInterval(() => this.advance(), this.intervalValue)
  }

  stop () {
    if (this.timer) {
      window.clearInterval(this.timer)
      this.timer = null
    }
  }

  scheduleResume () {
    this.clearResumeTimer()
    this.resumeTimer = window.setTimeout(() => {
      this.resume()
    }, this.intervalValue)
  }

  clearResumeTimer () {
    if (this.resumeTimer) {
      window.clearTimeout(this.resumeTimer)
      this.resumeTimer = null
    }
  }

  clearResetTimer () {
    if (this.resetTimer) {
      window.clearTimeout(this.resetTimer)
      this.resetTimer = null
    }
  }

  get reducedMotion () {
    return prefersReducedMotion()
  }
}
